import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, ScanLine, Film, Search, Building2, User, ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";

const scoreColor = (s) => (s >= 75 ? "text-gold" : s >= 50 ? "text-cream" : "text-muted-foreground");

export default function AdminConsole() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [issue, setIssue] = useState("");

  const load = () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (issue) params.issue = issue;
    api.get("/admin/scans", { params }).then((r) => setRows(r.data.scans)).catch(() => setRows([]));
  };

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, issue]);

  const maxCount = useMemo(() => stats?.top_issues?.[0]?.count || 1, [stats]);

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><ShieldCheck className="h-4 w-4" /> Admin Console</span>
            <h1 className="mt-2 font-heading text-4xl text-cream md:text-5xl">Client scalp records</h1>
            <p className="mt-2 text-muted-foreground">Every scan across your client base, in one place.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button className="rounded-full bg-gold px-6 py-5 text-charcoal hover:bg-gold/90" onClick={() => navigate("/admin/scan")} data-testid="admin-new-scan-btn">
              <ScanLine className="mr-2 h-4 w-4" /> New In-Salon Scan
            </Button>
            <Button variant="ghost" className="rounded-full border border-charcoal-border px-6 py-5 text-cream hover:text-gold" onClick={() => navigate("/gallery")} data-testid="admin-transformations-btn">
              <Film className="mr-2 h-4 w-4" /> Manage Transformations
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-charcoal-border bg-charcoal-surface p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total scans</p>
            <p className="mt-2 font-heading text-5xl text-gold" data-testid="stat-total">{stats?.total_scans ?? "—"}</p>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">This month</p>
            <p className="mt-1 font-heading text-3xl text-cream" data-testid="stat-month">{stats?.scans_this_month ?? "—"}</p>
          </div>
          <div className="rounded-2xl border border-charcoal-border bg-charcoal-surface p-6 lg:col-span-2" data-testid="top-issues">
            <p className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">Most common flagged issues</p>
            {!stats ? <Loader2 className="h-5 w-5 animate-spin text-gold" /> : stats.top_issues.length === 0 ? (
              <p className="text-sm text-muted-foreground">No flagged issues yet.</p>
            ) : (
              <div className="space-y-3">
                {stats.top_issues.map((it) => (
                  <div key={it.issue}>
                    <div className="mb-1 flex justify-between text-sm"><span className="text-cream">{it.issue}</span><span className="text-gold">{it.count} scan{it.count === 1 ? "" : "s"}</span></div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-charcoal">
                      <div className="h-full rounded-full bg-gold" style={{ width: `${(it.count / maxCount) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client name or email…" className="border-charcoal-border bg-charcoal-surface pl-9 text-cream focus-visible:ring-gold" data-testid="admin-search-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            {["", "Hydration", "Buildup", "Irritation", "Density", "Dandruff"].map((t) => (
              <button key={t || "all"} onClick={() => setIssue(t)} className={`rounded-full border px-4 py-2 text-xs transition-colors ${issue === t ? "border-gold bg-gold/10 text-gold" : "border-charcoal-border text-muted-foreground hover:text-cream"}`} data-testid={`admin-filter-${t || "all"}`}>
                {t || "All issues"}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-charcoal-border">
          <div className="hidden grid-cols-12 gap-3 bg-charcoal-surface px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:grid">
            <span className="col-span-3">Client</span><span className="col-span-2">Date</span><span className="col-span-1">Score</span><span className="col-span-4">Flagged</span><span className="col-span-2">Source</span>
          </div>
          {rows === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : rows.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground" data-testid="admin-scans-empty">No scans match your filters.</p>
          ) : (
            <div data-testid="admin-scans-table">
              {rows.map((s, i) => (
                <motion.div key={s.scan_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                  onClick={() => navigate(`/report/${s.scan_id}`)}
                  className="grid cursor-pointer grid-cols-1 gap-2 border-t border-charcoal-border bg-charcoal px-5 py-4 transition-colors hover:bg-charcoal-surface md:grid-cols-12 md:items-center md:gap-3" data-testid={`admin-scan-row-${s.scan_id}`}>
                  <div className="md:col-span-3">
                    <p className="text-sm text-cream">{s.client_name}</p>
                    <p className="text-xs text-muted-foreground">{s.client_email}</p>
                  </div>
                  <div className="text-xs text-muted-foreground md:col-span-2">{new Date(s.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</div>
                  <div className={`font-heading text-xl md:col-span-1 ${scoreColor(s.overall_score)}`}>{s.overall_score}</div>
                  <div className="flex flex-wrap gap-1.5 md:col-span-4">
                    {s.flagged.length === 0 ? <span className="text-xs text-muted-foreground">None</span> : s.flagged.map((f) => (
                      <span key={f} className="rounded-full border border-charcoal-border px-2 py-0.5 text-[10px] text-cream/80">{f}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between md:col-span-2 md:justify-start md:gap-2">
                    {s.source === "in_salon" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 px-2.5 py-1 text-[10px] text-gold"><Building2 className="h-3 w-3" /> In-Salon</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-charcoal-border px-2.5 py-1 text-[10px] text-muted-foreground"><User className="h-3 w-3" /> Self-serve</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-xs text-gold md:hidden">View <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
